import React from 'react'

import NiceModal from '@ebay/nice-modal-react'
import { Checkbox, CheckboxProps, Stack } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import { ModalShell, useModalControls } from '#components/Modals'
import { ILoadedScript } from '#state'
import {
  DEFAULT_CONTD_DISPLAY_TEXT,
  DEFAULT_MORE_DISPLAY_TEXT,
  DEFAULT_SCENE_CONTD_FOOTER_PREFIX,
  setMoresContds,
} from '#util'
import { moresContdsOverridesParser } from '#util/formats'

import styles from './MoresAndContinueds.module.scss'

const overrideFormValuesSchema = z.object({
  overrides: moresContdsOverridesParser,
})

type OverrideFormValues = z.infer<typeof overrideFormValuesSchema>

const MoresCheckbox = (props: CheckboxProps) => (
  <Checkbox
    classNames={{
      body: styles.MoresAndContinueds_checkboxBody,
      description: styles.MoresAndContinueds_checkboxDescription,
      label: styles.MoresAndContinueds_checkboxLabel,
    }}
    {...props}
  />
)

const MoresAndContinueds = ({ script }: { script: ILoadedScript }) => {
  const controls = useModalControls()

  const form = useForm<OverrideFormValues>({
    initialValues: {
      overrides: script.currentMoresContds ?? {},
    },
    validate: zodResolver(overrideFormValuesSchema),
  })

  const handleConfirm = form.onSubmit((values) => {
    if (form.isDirty('overrides')) {
      setMoresContds({ overrides: values.overrides, script })
      script.trackEvent('SCRIPT_MORES_CONTDS_CUSTOMIZED')
      script.pmEditor.editorManager?.reloadPlugins(script)
      script.pmEditor.rerender()
    }
    controls.onClose()
  })

  return (
    <ModalShell
      title="Mores and continueds"
      onConfirm={() => handleConfirm()}
      confirmLabel="Save"
      cancelLabel="Cancel"
      size={400}
      opened={controls.opened}
      onClose={controls.onClose}
    >
      <Stack px={5} pt={5} pb={10}>
        <MoresCheckbox
          label="Automatic character continueds"
          description={`${DEFAULT_CONTD_DISPLAY_TEXT} will display after character's name when dialogue is interrupted`}
          checked={!!form.values.overrides?.enableCharContds}
          onChange={(e) => {
            form.setFieldValue('overrides', {
              ...form.values.overrides,
              enableCharContds: e.target.checked,
            })
          }}
        />
        <MoresCheckbox
          label="Break dialogue between pages"
          description={`Allow dialogue to flow onto the next page, displaying ${DEFAULT_MORE_DISPLAY_TEXT} and  ${DEFAULT_CONTD_DISPLAY_TEXT}`}
          checked={!!form.values.overrides?.enableDialogueContds}
          onChange={(e) => {
            form.setFieldValue('overrides', {
              ...form.values.overrides,
              enableDialogueContds: e.target.checked,
              enableDialogueMores: e.target.checked,
            })
          }}
        />
        <MoresCheckbox
          label="Scene breaks"
          description={`Display ${DEFAULT_SCENE_CONTD_FOOTER_PREFIX} when a scene flows onto the next page`}
          checked={!!form.values.overrides?.enableSceneContds}
          onChange={(e) => {
            form.setFieldValue('overrides', {
              ...form.values.overrides,
              enableSceneContds: e.target.checked,
            })
          }}
        />
      </Stack>
    </ModalShell>
  )
}

export const MoresAndContinuedsModal = NiceModal.create(MoresAndContinueds)
